import React from 'react'
import { useLocation } from 'react-router-dom'
import { welcomeMsgVisibilityWidth, ToolConfigMode } from './constants'
import {
  CsvFileIcon,
  DocxFileIcon,
  InvalidFileIcon,
  PdfFileIcon,
  TxtFileIcon,
  XlsxFileIcon
} from '../icons/Icons'
import { IconFileTypePpt } from '@tabler/icons-react'

export const scrollToTarget = (element: HTMLElement | null) => {
  if (!element) return
  element.scrollIntoView({ block: 'start', behavior: 'smooth' })
}

export const hexToRgb = (hex: any) => {
  // Remove '#' if present
  hex = hex.replace(/^#/, '')
  // Parse short form (#abc) to long form (#aabbcc)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((x: any) => x + x)
      .join('')
  }
  const num = parseInt(hex, 16)
  const r = (num >> 16) & 255
  const g = (num >> 8) & 255
  const b = num & 255
  return `${r},${g},${b}`
}

export const formatUserInput = (userInput: string) => {
  return userInput.trim().replace(/\n/g, '<br />').replaceAll('  ', '&emsp;')
}

export const showWelcomePrompts = (AliWidthNum: number, isFullScreen = false) => {
  const hideChatWelcomeElements = document.querySelectorAll('.chat-welcome-hide-sm')

  const displayStyle = isFullScreen || AliWidthNum > welcomeMsgVisibilityWidth ? 'flex' : 'none'

  hideChatWelcomeElements.forEach((element: any) => {
    if (element) element.style.display = displayStyle
  })
}

export const removeActiveClasses = (element: any) => {
  const classesToRemove = Array.from(element.classList).filter((className: any) =>
    className.startsWith('active-')
  )

  classesToRemove.forEach((className) => {
    element.classList.remove(className)
  })
}

export const getFileDisplayName = (fileName: string) => {
  // Strip a leading numeric timestamp or GUID-style id segment (with or without dashes) that
  // upload/databox services prefix onto the stored file name, e.g. "1234567890_file.pdf" or
  // "c9632ef756fc49c3997242c7272d195b_file.pptx".
  return fileName.replace(
    /^(?:\d+|[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}|[0-9a-fA-F]{20,40})_/,
    ''
  )
}

// checkIsDrawerMode is a plain helper (not a hook) used and mocked across ~20 files as
// `checkIsDrawerMode()`; renaming to satisfy the "use" naming convention would require updating
// every call site and test mock, which is out of scope here.
export const checkIsDrawerMode = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location = useLocation()
  return (
    location?.pathname === '/workspace' ||
    location?.pathname?.includes('/data-marketplace') ||
    location?.pathname?.includes('/data-product-admin')
  )
}

// A favicon that never fires load/error (unreachable host, request hanging behind a slow proxy)
// would otherwise leave the promise pending forever. Cap the wait so one bad domain can't stall
// the citations list, and always clean up the pending timer/handlers.
export const validateImageUrl = async (url: string, timeoutMs = 4000): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image()
    let settled = false
    const finish = (result: boolean) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      img.onload = null
      img.onerror = null
      resolve(result)
    }
    const timer = setTimeout(() => finish(false), timeoutMs)
    img.onload = () => finish(true)
    img.onerror = () => finish(false)
    img.src = url
  })
}

// Extracts the main domain
export const fetchDomain = (url: string) => {
  try {
    const hostname = new URL(url).hostname
    // Remove 'www.' if present
    const cleanHost = hostname.replace(/^www\./, '')
    return cleanHost
  } catch {
    return null
  }
}

export const prepareCitations = async (citations: any[]) => {
  return Promise.all(
    citations.map(async (c) => {
      const domain = fetchDomain(c.url || c.reference)
      if (!domain) return { ...c, icon: null }
      const faviconUrl = `https://${domain}/favicon.ico`
      const isValid = await validateImageUrl(faviconUrl)
      return { ...c, icon: isValid ? faviconUrl : null }
    })
  )
}

// Convert a base64 data URL to a Blob
export const base64ToBlob = (dataUrl: string) => {
  try {
    const parts = dataUrl.split(',')
    const byteString = atob(parts[1])
    const mimeString = parts[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)
    return new Blob([ab], { type: mimeString })
  } catch (err) {
    console.error('base64ToBlob error', err)
    return null
  }
}

// Highlight search terms function for search results
export const highlightSearchTerm = (text: string, searchTerm: string) => {
  if (!searchTerm || !searchTerm.trim()) return text

  // Split search term into individual words
  const searchWords = searchTerm.toLowerCase().trim().split(/\s+/)
  const highlightedText = text

  // Create a regex that matches any of the search words
  const wordsRegex = searchWords
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')

  const regex = new RegExp(`(${wordsRegex})`, 'gi')
  const parts = highlightedText.split(regex)

  return parts.map((part, index) =>
    regex.test(part)
      ? React.createElement(
          'span',
          {
            key: index,
            className: 'theme-search-highlight'
          },
          part
        )
      : part
  )
}

// Bold search terms function for search results (no background highlight)
export const boldSearchTerm = (text: string, searchTerm: string) => {
  if (!searchTerm || !searchTerm.trim()) return text

  const searchWords = searchTerm.toLowerCase().trim().split(/\s+/)
  const wordsRegex = searchWords
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')

  const regex = new RegExp(`(${wordsRegex})`, 'gi')
  const parts = text?.split(regex)

  return parts?.map((part, index) =>
    regex.test(part)
      ? React.createElement(
          'strong',
          { key: index, className: 'font-extrabold text-gray-900' },
          part
        )
      : part
  )
}

export const getFileIcon = (fileName: string): React.JSX.Element => {
  const extension = fileName.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'pdf':
      return <PdfFileIcon size={20} className="text-red-500" />
    case 'doc':
    case 'docx':
      return <DocxFileIcon size={20} className="text-blue-500" />
    case 'xls':
    case 'xlsx':
      return <XlsxFileIcon size={20} className="text-green-500" />
    case 'csv':
      return <CsvFileIcon size={20} className="text-green-500" />
    case 'txt':
      return <TxtFileIcon size={20} className="text-gray-500" />
    case 'ppt':
    case 'pptx':
      return <IconFileTypePpt size={20} className="text-yellow-500" />
    default:
      return <InvalidFileIcon size={20} className="text-gray-500" />
  }
}

/**
 * Generic helper to resolve tool config from an MCP server object.
 * Uses exact toolName match or fallback if tools are unavailable.
 */
const resolveToolConfig = (
  mcpServer: any,
  toolName: string
): { mcpServerId: string; toolName: string } | undefined => {
  const mcpServerId = mcpServer?.id || mcpServer?.mcpServerId
  if (!mcpServerId) return undefined

  const tools = Array.isArray(mcpServer?.tools) ? mcpServer.tools : []

  // Try to find exact match by toolName
  const matchedTool = tools.find(
    (tool: any) => tool?.toolName === toolName || tool?.name === toolName
  )

  return {
    mcpServerId,
    toolName: matchedTool?.toolName || matchedTool?.name || toolName
  }
}

/**
 * Resolve the web-search tool config from an MCP server object.
 * Shared by ChatInput (submit), chatService (regenerate), and ChatHistory (hydration).
 */
export const resolveWebSearchToolConfig = (
  webSearchMcpServer: any
): { mcpServerId: string; toolName: string } | undefined => {
  return resolveToolConfig(webSearchMcpServer, 'search_web')
}

/**
 * Resolve the Snowflake (Structured Data) tool config from an MCP server object.
 * Returns both GetQuery and ExecuteQuery tools with the same mcpServerId.
 */
export const resolveSnowflakeToolConfig = (
  snowflakeMcpServer: any
): Array<{ mcpServerId: string; toolName: string }> | undefined => {
  const mcpServerId = snowflakeMcpServer?.id || snowflakeMcpServer?.mcpServerId
  if (!mcpServerId) return undefined

  const tools = Array.isArray(snowflakeMcpServer?.tools) ? snowflakeMcpServer.tools : []

  // Map both tool names to their configs in a single pass
  return ['list_semantic_views', 'generate_sql_query', 'execute_sql_query'].map((name) => {
    const tool = tools.find((t: any) => t?.toolName === name || t?.name === name)
    return {
      mcpServerId,
      toolName: tool?.toolName || tool?.name || name
    }
  })
}

/**
 * Resolve the SmartInsights tool config from an MCP server object.
 */
export const resolveSmartInsightsToolConfig = (
  smartInsightsMcpServer: any
): { mcpServerId: string; toolName: string } | undefined => {
  return resolveToolConfig(smartInsightsMcpServer, 'search_documents')
}

/**
 * Detect whether a tool config refers to the web-search tool. Mirrors the detection used when
 * hydrating a thread in ChatHistory so the two stay in sync.
 */
const isWebSearchToolConfig = (config?: { toolName?: string }): boolean => {
  const toolName = `${config?.toolName || ''}`.toLowerCase()
  return (
    toolName.includes('websearch') ||
    toolName.includes('web_search') ||
    (toolName.includes('web') && toolName.includes('search'))
  )
}

/**
 * Apply toolConfiguration to AG UI forwardedProps based on MCP tool configs.
 * - 0 configs: AUTO mode
 * - Any data-source tool (structured/unstructured) present: AUTO mode, so the LLM can still pick
 *   other tools (e.g. code interpreter) when a prompt needs them.
 * - Only web search: FORCE_SPECIFIC (single) / REQUIRED_ANY (multiple), since web search is only
 *   sent when the user explicitly enabled it.
 */
export const applyToolConfiguration = (
  forwardedProps: Record<string, any>,
  mcpToolConfigs?: Array<{ mcpServerId: string; toolName: string }>
): Record<string, any> => {
  const validConfigs = (mcpToolConfigs || []).filter(
    (config) => config?.mcpServerId && config?.toolName
  )

  const hasDataSourceTool = validConfigs.some((config) => !isWebSearchToolConfig(config))

  if (validConfigs.length === 0 || hasDataSourceTool) {
    forwardedProps.toolConfiguration = { mode: ToolConfigMode.AUTO }
  } else if (validConfigs.length === 1) {
    forwardedProps.parallelToolCalls = false
    forwardedProps.toolConfiguration = {
      mode: ToolConfigMode.FORCE_SPECIFIC,
      requiredTool: {
        mcpServerId: validConfigs[0]?.mcpServerId,
        toolName: validConfigs[0]?.toolName
      }
    }
  } else {
    forwardedProps.parallelToolCalls = false
    forwardedProps.toolConfiguration = {
      mode: ToolConfigMode.REQUIRED_ANY,
      allowedTools: validConfigs.map((config) => ({
        mcpServerId: config?.mcpServerId,
        toolName: config?.toolName
      }))
    }
  }
  return forwardedProps
}

/**
 * Convert customSelection to OData filter query for tags (optimized format)
 * @param customSelection - Object containing databox selections with optional documentIds
 * @param documentsMap - Map of documentId to cognitiveSearchId for looking up document identifiers
 * @returns OData filter query string
 * @example
 * // Databox with specific documents
 * convertCustomSelectionToODataFilter(
 *   { databox: [{ id: "140", documentIds: [6081, 5946] }] },
 *   { 6081: 'doc1.xlsx', 5946: 'doc2.docx' }
 * )
 * // Returns: "(tags/any(tag: tag eq 'databoxid:140') and (tags/any(tag: tag eq 'cognitivesearchid:doc1.xlsx') or tags/any(tag: tag eq 'cognitivesearchid:doc2.docx')))"
 *
 * // Entire databox selected
 * convertCustomSelectionToODataFilter(
 *   { databox: [{ id: "106" }] },
 *   {}
 * )
 * // Returns: "(tags/any(tag: tag eq 'databoxid:106'))"
 */
export const convertCustomSelectionToODataFilter = (
  customSelection: {
    databox?: Array<{
      id: string | number
      documentIds?: Array<string | number>
    }>
  },
  documentsMap: Record<string | number, string> = {}
): string => {
  if (!customSelection?.databox?.length) return ''

  const databoxConditions = customSelection.databox.map((databox) => {
    const databoxFilter = `tags/any(tag: tag eq 'databoxid:${databox.id}')`

    // If specific documents are selected within the databox
    if (databox.documentIds?.length) {
      const documentConditions = databox.documentIds
        .map((docId) => documentsMap[docId])
        .filter(Boolean)
        .map((cogId) => `tags/any(tag: tag eq 'cognitivesearchid:${cogId}')`)

      if (documentConditions.length > 0) {
        return `${databoxFilter} and (${documentConditions.join(' or ')})`
      }
    }

    // Entire databox selected (or no valid documents found)
    return databoxFilter
  })

  // Every databox condition is wrapped in its own parentheses, even when there's only one,
  // then joined with OR when there are multiple.
  return databoxConditions.map((condition) => `(${condition})`).join(' or ')
}

/**
 * Extract all cognitive search IDs from an OData filter string
 * Used to fetch document details before processing customSelection
 * @param odataFilter - OData filter string
 * @returns Array of unique cognitive search IDs
 * @example
 * extractCognitiveSearchIdsFromOData("tags/any(tag: tag eq 'cognitivesearchid:abc123')")
 * // Returns: ["abc123"]
 */
export const extractCognitiveSearchIdsFromOData = (odataFilter: string): string[] => {
  if (!odataFilter?.trim()) return []

  // Use matchAll with Set for automatic deduplication
  const matches = odataFilter.matchAll(/cognitivesearchid:([^\s']+)/g)
  return [...new Set(Array.from(matches, (m) => m[1]))]
}

/**
 * Extract all databox IDs from an OData filter string
 * Used since dataBoxIds is no longer sent separately - the filter is the source of truth
 * @param odataFilter - OData filter string
 * @returns Array of unique databox IDs
 * @example
 * extractDataboxIdsFromOData("tags/any(tag: tag eq 'databoxid:140')")
 * // Returns: ["140"]
 */
export const extractDataboxIdsFromOData = (odataFilter: string): string[] => {
  if (!odataFilter?.trim()) return []

  const matches = odataFilter.matchAll(/databoxid:([^\s')]+)/g)
  return [...new Set(Array.from(matches, (m) => m[1]))]
}

/**
 * Build an OData filter from selected databoxes, honoring per-databox file-level selection
 * (`selectedItems`) when present - same file-selection shape the "Connect Databox" modal
 * produces (`{ type: 'file', smartDocsDocumentId, cognitiveSearchId }`). A databox with no
 * file-type `selectedItems` is treated as fully selected (whole databox).
 * @example
 * buildDataboxODataFilter([{ id: '140' }])
 * // Returns: "(tags/any(tag: tag eq 'databoxid:140'))"
 * buildDataboxODataFilter([{ id: '140', selectedItems: [{ type: 'file', smartDocsDocumentId: 1, cognitiveSearchId: 'doc1' }] }])
 * // Returns: "(tags/any(tag: tag eq 'databoxid:140') and (tags/any(tag: tag eq 'cognitivesearchid:doc1')))"
 */
export const buildDataboxODataFilter = (
  databoxes: Array<{
    id: string
    smartDocsDataboxId?: string
    selectedItems?: Array<{
      type: string
      smartDocsDocumentId?: string | number
      cognitiveSearchId?: string
    }>
  }>
): string => {
  if (!databoxes?.length) return ''

  const documentsMap: Record<string | number, string> = {}
  databoxes.forEach((db) => {
    db.selectedItems?.forEach((item) => {
      // Use != null (not truthy) so a valid but falsy id (e.g. 0) isn't silently dropped
      if (
        item.type === 'file' &&
        item.smartDocsDocumentId !== null &&
        item.smartDocsDocumentId !== undefined &&
        item.cognitiveSearchId
      ) {
        documentsMap[item.smartDocsDocumentId] = item.cognitiveSearchId
      }
    })
  })

  const customSelection = {
    databox: databoxes.map((db) => {
      const fileIds = (db.selectedItems || [])
        .filter(
          (item) =>
            item.type === 'file' &&
            item.smartDocsDocumentId !== null &&
            item.smartDocsDocumentId !== undefined
        )
        .map((item) => item.smartDocsDocumentId as string | number)

      return {
        id: db.smartDocsDataboxId || db.id,
        ...(fileIds.length > 0 && { documentIds: fileIds })
      }
    })
  }

  return convertCustomSelectionToODataFilter(customSelection, documentsMap)
}

/**
 * Rebuild the `dataProductIds` (flat) and `customSelection.dataProducts` (grouped) scope fields
 * from the current standalone data product selection. Any databox-nested groups on the original
 * config are preserved untouched (they aren't editable from the flat chat data-source picker) -
 * only the standalone (`databoxId: null`) group is replaced with the current selection.
 * @param originalConfig - Previously parsed scopeConfiguration (or null/undefined if none)
 * @param currentStandaloneIds - Currently selected standalone data product IDs
 */
export const buildDataProductScopeFields = (
  originalConfig: any,
  currentStandaloneIds: string[]
): { dataProductIds: string[]; customSelection?: { dataProducts: any[] } } => {
  const originalGroups: any[] = Array.isArray(originalConfig?.customSelection?.dataProducts)
    ? originalConfig.customSelection.dataProducts
    : []

  // Preserve databox-nested groups untouched
  const nestedGroups = originalGroups.filter(
    (group) => group?.databoxId !== null && group?.databoxId !== undefined
  )

  const groups = [...nestedGroups]
  if (currentStandaloneIds.length > 0) {
    groups.push({ databoxId: null, dataProductIds: currentStandaloneIds })
  }

  const allIds = new Set<string>(currentStandaloneIds.map((id) => String(id)))
  nestedGroups.forEach((group: any) =>
    (group?.dataProductIds || []).forEach((id: any) => allIds.add(String(id)))
  )

  return {
    dataProductIds: Array.from(allIds),
    ...(groups.length > 0 && { customSelection: { dataProducts: groups } })
  }
}

// Parses a human-formatted size string (e.g. "2.4 MB", "500KB", "1 GB", "800 B") back into bytes.
// Returns 0 for anything it can't recognize instead of throwing, so a single bad/missing value
// doesn't break a sum across many files.
export const parseFormattedSizeToBytes = (formatted?: string | null): number => {
  if (!formatted) return 0
  const match = String(formatted)
    .trim()
    .match(/^([\d.]+)\s*(B|KB|MB|GB|TB)$/i)
  if (!match) return 0
  const value = parseFloat(match[1])
  if (Number.isNaN(value)) return 0
  const unit = match[2].toUpperCase()
  const multipliers: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4
  }
  return value * (multipliers[unit] ?? 1)
}

// Formats a byte count back into a human-readable string, matching the "2.4 MB" style used
// elsewhere in the app.
export const formatBytesToSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exponent // eslint-disable-line no-mixed-operators
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`
}

// Sums a list of already-formatted size strings (parsing each back to bytes first) and returns
// the total re-formatted as a single human-readable string.
export const sumFormattedSizes = (sizes: Array<string | undefined | null>): string => {
  const totalBytes = sizes.reduce((sum, size) => sum + parseFormattedSizeToBytes(size), 0)
  return formatBytesToSize(totalBytes)
}
